<template>
  <app-dialog
    v-model="visible"
    :title="t('tokens.top_up_dialog.title')"
    :description="t('tokens.top_up_dialog.description')"
    size="sm"
    :dismissible="canDismiss"
    :show-close="canDismiss"
    @close="handleDialogClose"
  >
    <div class="space-y-4" data-test="token-top-up-dialog">
      <app-alert
        :tone="statusTone"
        :title="statusTitle"
        data-test="token-top-up-status"
      >
        <span :data-test="`token-top-up-status-${status}`">{{ statusMessage }}</span>
      </app-alert>

      <div class="space-y-2" data-test="token-top-up-pack-list">
        <p class="text-sm font-medium text-theme-text">
          {{ t('tokens.top_up_dialog.pack_label') }}
        </p>
        <p class="text-xs text-theme-text-secondary">
          {{ t('tokens.top_up_dialog.pack_help') }}
        </p>
        <p v-if="tokenStore.topUpOptionsLoading" class="text-sm text-theme-text-secondary">
          {{ t('tokens.top_up_dialog.loading_packs') }}
        </p>
        <div v-else class="grid gap-2">
          <button
            v-for="pack in packs"
            :key="pack.key"
            type="button"
            class="flex items-center justify-between gap-3 rounded-theme-sm border px-3 py-2 text-left text-sm transition"
            :class="selectedPackKey === pack.key
              ? 'border-theme-primary bg-theme-primary/10 text-theme-text'
              : 'border-theme-border bg-theme-bg-subtle text-theme-text-secondary hover:border-theme-primary/60 hover:bg-theme-surface-raised'"
            :aria-pressed="selectedPackKey === pack.key"
            :data-key="pack.key"
            data-test="token-top-up-pack-option"
            @click="selectedPackKey = pack.key"
          >
            <span class="font-medium text-theme-text">
              {{ formatNumber(pack.tokens) }} {{ t('tokens.top_up_dialog.tokens') }}
            </span>
            <span>{{ formatCurrency(pack.amount, pack.currency) }}</span>
          </button>
        </div>
      </div>

      <div class="rounded-theme-sm border border-theme-border bg-theme-bg-subtle p-3 text-sm text-theme-text-secondary">
        <div class="flex items-center justify-between gap-3">
          <span>{{ t('tokens.top_up_dialog.starting_balance') }}</span>
          <span class="font-medium text-theme-text" data-test="token-top-up-starting-balance">
            {{ formatNumber(startingBalance) }}
          </span>
        </div>
        <div class="mt-2 flex items-center justify-between gap-3">
          <span>{{ t('tokens.top_up_dialog.current_balance') }}</span>
          <span class="font-medium text-theme-text" data-test="token-top-up-current-balance">
            {{ formatNumber(currentBalance) }}
          </span>
        </div>
      </div>

      <a
        v-if="checkoutUrl"
        :href="checkoutUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex text-sm font-medium text-theme-link hover:text-theme-link-hover"
        data-test="token-top-up-checkout-link"
      >
        {{ t('tokens.top_up_dialog.checkout_link') }}
      </a>
    </div>

    <template #footer>
      <app-button
        variant="outline"
        tone="neutral"
        :label="t(status === 'success' ? 'common.close' : 'tokens.top_up_dialog.keep_working')"
        data-test="token-top-up-close"
        @click="close"
      />
      <app-button
        v-if="status !== 'success'"
        variant="solid"
        tone="primary"
        :label="t('tokens.top_up_dialog.checkout_button')"
        :loading="status === 'opening'"
        :disabled="!selectedPackKey || tokenStore.topUpOptionsLoading || status === 'opening' || status === 'waiting'"
        data-test="token-top-up-checkout-button"
        @click="beginCheckout"
      />
    </template>
  </app-dialog>
</template>

<script setup>
  import AppAlert from '@/components/ui/feedback/AppAlert.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppDialog from '@/components/ui/overlays/AppDialog.vue';
  import { useTokenBalanceStore } from '@/stores/useTokenBalanceStore';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const POLL_INTERVAL_MS = 2500;
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;

  const { t, locale } = useI18n();
  const tokenStore = useTokenBalanceStore();

  const visible = ref(false);
  const status = ref('idle');
  const checkoutUrl = ref('');
  const errorMessage = ref('');
  const startingBalance = ref(0);
  const startingPurchasedBalance = ref(0);
  const currentBalance = ref(0);
  const requiredTokens = ref(0);
  const selectedPackKey = ref('');

  let checkoutWindow = null;
  let pollRunId = 0;

  const canDismiss = computed(() => status.value !== 'opening' && status.value !== 'waiting');

  const statusTone = computed(() => {
    if (status.value === 'success') return 'success';
    if (status.value === 'error') return 'danger';
    if (status.value === 'blocked') return 'warning';
    return 'info';
  });

  const statusTitle = computed(() => t(`tokens.top_up_dialog.status.${status.value}.title`));
  const statusMessage = computed(() => {
    if (status.value === 'error' && errorMessage.value) {
      return errorMessage.value;
    }
    return t(`tokens.top_up_dialog.status.${status.value}.message`);
  });
  const packs = computed(() => tokenStore.topUpPacks || []);

  function numberFrom(value) {
    const numeric = Number(value ?? 0);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  function formatNumber(value) {
    try {
      return numberFrom(value).toLocaleString(locale.value);
    } catch {
      return numberFrom(value).toLocaleString();
    }
  }

  function formatCurrency(minorAmount, currency = 'eur') {
    const amount = numberFrom(minorAmount) / 100;
    try {
      return new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency: String(currency || 'eur').toUpperCase(),
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${String(currency || 'eur').toUpperCase()}`;
    }
  }

  function accountTotal(account = tokenStore.account) {
    return numberFrom(account?.total_balance ?? tokenStore.totalBalance);
  }

  function accountPurchased(account = tokenStore.account) {
    return numberFrom(account?.purchased_balance ?? tokenStore.purchasedBalance);
  }

  function close() {
    pollRunId += 1;
    visible.value = false;
    status.value = 'idle';
    checkoutWindow = null;
  }

  function handleDialogClose() {
    if (canDismiss.value) {
      close();
    }
  }

  function open(event) {
    pollRunId += 1;
    const runId = pollRunId;
    checkoutUrl.value = '';
    errorMessage.value = '';
    requiredTokens.value = numberFrom(event?.detail?.requiredTokens);
    startingBalance.value = numberFrom(event?.detail?.balance ?? accountTotal());
    startingPurchasedBalance.value = numberFrom(event?.detail?.purchasedBalance ?? accountPurchased());
    currentBalance.value = startingBalance.value;
    visible.value = true;
    status.value = 'idle';
    selectedPackKey.value = defaultPackKey(event?.detail);

    tokenStore
      .loadTopUpOptions()
      .then(() => {
        if (runId !== pollRunId) return;
        selectedPackKey.value = defaultPackKey(event?.detail);
      })
      .catch((error) => {
        if (runId !== pollRunId) return;
        errorMessage.value =
          error?.response?.data?.message ||
          error?.message ||
          t('tokens.top_up_dialog.status.error.message');
        status.value = 'error';
      });
  }

  function defaultPackKey(detail = {}) {
    const availablePacks = packs.value;
    if (!availablePacks.length) {
      return tokenStore.defaultTopUpPackKey || 'topup-1000';
    }

    const sorted = [...availablePacks].sort((a, b) => numberFrom(a.tokens) - numberFrom(b.tokens));
    const required = numberFrom(detail?.requiredTokens ?? requiredTokens.value);
    if (required > 0) {
      const covering = sorted.find((pack) => numberFrom(pack.tokens) >= required);
      if (covering) {
        return covering.key;
      }
    }

    return (
      sorted.find((pack) => pack.key === tokenStore.defaultTopUpPackKey)?.key ||
      sorted.find((pack) => pack.key === 'topup-1000')?.key ||
      sorted[0]?.key ||
      ''
    );
  }

  function beginCheckout() {
    if (!selectedPackKey.value || status.value === 'opening' || status.value === 'waiting') return;
    pollRunId += 1;
    status.value = 'opening';
    checkoutWindow = window.open('about:blank', '_blank');
    startCheckoutAndPolling(pollRunId);
  }

  async function startCheckoutAndPolling(runId) {
    try {
      const checkout = await tokenStore.createTopUpCheckout(selectedPackKey.value);
      if (runId !== pollRunId) return;

      checkoutUrl.value = checkout.url;
      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.location.href = checkout.url;
        status.value = 'waiting';
      } else {
        status.value = 'blocked';
      }
      pollForTokenCredit(runId);
    } catch (error) {
      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.close();
      }
      if (runId !== pollRunId) return;
      errorMessage.value =
        error?.response?.data?.message ||
        error?.message ||
        t('tokens.top_up_dialog.status.error.message');
      status.value = 'error';
    }
  }

  async function pollForTokenCredit(runId) {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      if (runId !== pollRunId || !visible.value) return;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (runId !== pollRunId || !visible.value) return;

      try {
        const account = await tokenStore.refresh({ force: true });
        const total = accountTotal(account);
        const purchased = accountPurchased(account);
        currentBalance.value = total;
        if (total > startingBalance.value || purchased > startingPurchasedBalance.value) {
          status.value = 'success';
          window.dispatchEvent(
            new CustomEvent('tokens:top-up:completed', {
              detail: {
                beforeBalance: startingBalance.value,
                afterBalance: total,
                beforePurchasedBalance: startingPurchasedBalance.value,
                afterPurchasedBalance: purchased,
              },
            })
          );
          return;
        }
      } catch {
        // Keep polling; Stripe webhook settlement can lag behind the redirect.
      }
    }

    if (runId === pollRunId && visible.value && status.value !== 'success') {
      status.value = 'error';
      errorMessage.value = t('tokens.top_up_dialog.status.timeout.message');
    }
  }

  onMounted(() => {
    window.addEventListener('tokens:top-up', open);
  });

  onUnmounted(() => {
    pollRunId += 1;
    window.removeEventListener('tokens:top-up', open);
  });
</script>
